import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { clearClientAuth } from './auth-session';

// Production (Nginx same-origin): empty base URL → /api/... on this host.
// Local dev: Vite on :5173, API on :3000.
const API_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD ? '' : 'http://localhost:3000');

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// ─── Request Interceptor ──────────────────────────────────────────────────────

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptor ─────────────────────────────────────────────────────

function isLoginRequest(error: AxiosError): boolean {
  const url = error.config?.url ?? '';
  return url.includes('/auth/login');
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401 && !isLoginRequest(error)) {
      // Clear ALL auth sources (token keys + zustand persist) so /login
      // does not immediately redirect back into a protected route loop.
      clearClientAuth();

      const onLoginPage = window.location.pathname.startsWith('/login');
      if (!onLoginPage) {
        window.location.assign('/login');
      }
    }

    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data);
    }

    return Promise.reject(error);
  }
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function extractData<T>(response: { data: T }): T {
  return response.data;
}

export function buildQueryString(params?: Record<string, unknown> | object | null): string {
  if (!params) return '';
  const query = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });
  return query.toString() ? `?${query.toString()}` : '';
}

export default api;
